// libs
import React, { memo, useRef, useState, useEffect, useMemo, useCallback } from "react";

// contexts
import FakeScrollerContext from "./FakeScrollerContext";

// helpers
import { getHandleTop, getScrollTop } from "./helpers";

// styled
import { Container, FkContainer, FkInnerContainer, FkHandle } from "./styles";

// defs
interface IFakeScroller {
    children: React.ReactNode;
    className?: string;
}

const MINIMUM_HANDLE_HEIGHT = 30;
const SCROLL_HANDLE = "Scroll handle";

function FakeScroller({ children, className = "" }: IFakeScroller) {
    let rAFTimer: number | null = null;
    const el = useRef<HTMLDivElement>(null);
    const elHandle = useRef<HTMLButtonElement>(null);
    const animationHandle = useRef<number>(null);

    const [scrollHandleHeight, setScrollHandle] = useState(0);
    const [canShowHandle, setShowHandle] = useState(false);

    const [containerHeight, setContainerHeight] = useState(0);
    const [containerScrollHeight, setContainerScrollHeight] = useState(0);
    const [containerScrollTop, setContainerScrollTop] = useState(0);

    const [dragStart, setDragStart] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [dragStartScrollTop, setDragStartScrollTop] = useState(0);

    // calculate dimensions - container height, scroll height - can show scroll handle
    const calculateDimensions = useCallback(() => {
        if (el.current === null) {
            return;
        }

        // on children change scroll to TOP
        el.current.scrollTop = 0;

        const { height } = el.current.getBoundingClientRect();
        const scrollHeight = el.current.scrollHeight;

        setContainerHeight(height);
        setContainerScrollHeight(scrollHeight);

        if (scrollHeight <= height) {
            setShowHandle(false);
            return;
        }

        const handleHeight = Math.max(
            Math.ceil((height * height) / scrollHeight),
            MINIMUM_HANDLE_HEIGHT
        );
        setScrollHandle(handleHeight);
        setShowHandle(true);
    }, []);

    useEffect(() => {
        animationHandle.current = window.requestAnimationFrame(() => {
            calculateDimensions();
        });

        return () => {
            if (animationHandle.current !== null) {
                window.cancelAnimationFrame(animationHandle.current);
            }
        };
    }, [calculateDimensions, children]);

    // on scrollTop change - change scroll handle top
    useEffect(() => {
        const processFrame = window.requestAnimationFrame(() => {
            if (
                elHandle.current === null ||
                el.current === null ||
                containerScrollHeight === 0 ||
                containerHeight === 0
            ) {
                return;
            }

            const handleTop = getHandleTop({
                containerScrollTop,
                containerHeight,
                containerScrollHeight,
                scrollHandleHeight,
            });

            elHandle.current.style.transform = `translateY(${handleTop}px)`;
        });

        () => {
            window.cancelAnimationFrame(processFrame);
        };
    }, [containerScrollTop, containerHeight, containerScrollHeight, scrollHandleHeight]);

    // on handle drag update the content scrollTop
    useEffect(() => {
        let processFrame: number;
        const onMouseMove = (e: MouseEvent) => {
            if (processFrame !== undefined) {
                window.cancelAnimationFrame(processFrame);
            }

            processFrame = window.requestAnimationFrame(() => {
                if (el.current === null) {
                    return;
                }

                const newScrollTop = getScrollTop({
                    dragStartY,
                    dragStartScrollTop,
                    containerScrollHeight,
                    containerHeight,
                    currentDragY: e.clientY,
                });

                el.current.scrollTo(0, newScrollTop);
            });
        };

        if (dragStart) {
            window.addEventListener("mousemove", onMouseMove, { passive: true });
        }

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.cancelAnimationFrame(processFrame);
        };
    }, [dragStart, dragStartY, dragStartScrollTop, containerHeight, containerScrollHeight]);

    // on drag stop - clear chores
    useEffect(() => {
        const onMouseUp = () => {
            document.body.style.userSelect = "";
            setDragStart(false);
        };

        if (dragStart) {
            window.addEventListener("mouseup", onMouseUp, { passive: true });
        }

        return () => {
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragStart]);

    const handleStyle = useMemo(
        () => ({
            height: `${scrollHandleHeight}px`,
        }),
        [scrollHandleHeight]
    );

    const calculateDimensionsCallback = () => {
        if (animationHandle.current !== null) {
            window.cancelAnimationFrame(animationHandle.current);
        }

        calculateDimensions();
    };

    const onContainerScroll = () => {
        if (canShowHandle === false) {
            return;
        }

        if (rAFTimer !== null) {
            window.cancelAnimationFrame(rAFTimer);
        }

        rAFTimer = window.requestAnimationFrame(() => {
            rAFTimer = null;
            if (el.current === null) {
                return;
            }

            setContainerScrollTop(el.current.scrollTop);
        });
    };

    const onMouseDown: React.MouseEventHandler = e => {
        document.body.style.userSelect = "none";
        setDragStart(true);
        setDragStartY(e.clientY);
        setDragStartScrollTop(containerScrollTop);
    };

    return (
        <Container onScroll={onContainerScroll} className={className} data-test="fs-container">
            <FkContainer>
                <FakeScrollerContext.Provider
                    value={{ calculateDimensions: calculateDimensionsCallback }}
                >
                    <FkInnerContainer ref={el}>{children}</FkInnerContainer>
                </FakeScrollerContext.Provider>
            </FkContainer>
            {canShowHandle && (
                <FkHandle
                    ref={elHandle}
                    className={dragStart ? "dragging" : ""}
                    style={handleStyle}
                    onMouseDown={onMouseDown}
                    aria-label={SCROLL_HANDLE}
                    data-test="fs-drag-handle"
                />
            )}
        </Container>
    );
}

export default memo(FakeScroller);
